<template>
  <Page>
    <div class="space-y-2">
      <div id="user-management-header" class="bg-background-card rounded-lg shadow-card overflow-hidden p-6 flex items-center justify-between">
        <div class="flex items-center">
          <div class="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center mr-4">
            <i class="fa-solid fa-users text-xl"></i>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-primary">User Management</h1>
            <p class="text-secondary">Manage user accounts, roles, and permissions</p>
          </div>
        </div>
        <div class="flex space-x-3">
          <button id="export-users-btn" class="bg-background text-secondary rounded-full px-4 py-2 text-sm font-medium hover:bg-primary hover:text-white transition-colors flex items-center" @click="exportUsers">
            <i class="fa-solid fa-file-export mr-2"></i>
            Export CSV
          </button>
          <button id="import-users-btn" class="bg-background text-secondary rounded-full px-4 py-2 text-sm font-medium hover:bg-primary hover:text-white transition-colors flex items-center" @click="openImportModal">
            <i class="fa-solid fa-file-import mr-2"></i>
            Import CSV
          </button>
          <button id="create-user-btn" class="bg-accent text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-opacity-90 transition-colors flex items-center" @click="openCreateUserModal">
            <i class="fa-solid fa-user-plus mr-2"></i>
            Create User
          </button>
          <button id="bulk-create-btn" class="bg-primary text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-opacity-90 transition-colors flex items-center" @click="openBulkCreateModal">
            <i class="fa-solid fa-users-gear mr-2"></i>
            Bulk Create
          </button>
        </div>
      </div>

      <div style="margin-top: 0.5rem;">
        <!-- Tools Section -->
        <div id="user-management-tools" class="bg-background-card rounded-lg shadow-card overflow-hidden p-6 flex justify-between items-center mb-2">
          <!-- Search and Filter -->
          <UserSearchAndFilter />

          <!-- Bulk Actions -->
          <div class="flex items-center space-x-3">
            <button
              id="bulk-action-btn"
              @click="openBulkModal"
              :disabled="selectedUserCount === 0"
              :class="[
                'rounded-full px-4 py-2.5 text-sm font-medium flex items-center transition-colors',
                selectedUserCount === 0
                  ? 'bg-background text-secondary opacity-50 cursor-not-allowed'
                  : 'bg-background text-secondary hover:bg-primary hover:text-white'
              ]"
            >
              <i class="fa-solid fa-users-cog mr-2"></i>
              Bulk Actions
              <Badge class="ml-2" :value="selectedUserCount" />
            </button>
            <button id="refresh-btn" class="bg-background text-secondary rounded-full w-10 h-10 flex items-center justify-center hover:bg-primary hover:text-white transition-colors" @click="refreshUserList">
              <i class="fa-solid fa-sync-alt"></i>
            </button>
          </div>
        </div>

        <!-- User List Table -->
        <div id="user-list-table" class="bg-background-card rounded-lg shadow-card overflow-hidden h-full">
          <UserTable @update:selectedCount="selectedUserCount = $event" />
        </div>
      </div>
    </div>

    <!-- Bulk Actions Modal -->
    <BulkActionsModal
      :visible="bulkModalVisible"
      :selectedCount="selectedUserCount"
      @close="closeBulkModal"
    />

    <!-- Bulk Create Users Modal -->
    <BulkCreateUsersModal
      :visible="bulkCreateModalVisible"
      @close="closeBulkCreateModal"
    />

    <!-- Import Users Modal -->
    <ImportUsersModal
      :visible="importModalVisible"
      @close="closeImportModal"
    />

    <!-- Create User Modal -->
    <CreateUserModal :visible="createUserModalVisible" @close="closeCreateUserModal" />
  </Page>
</template>


<script setup lang="ts">

definePage({
  meta: {
    requiresProject: false,
  },
});

import { ref, onMounted } from 'vue';
import UserTable from '../components/UserTable.vue';
import UserSearchAndFilter from '../components/UserSearchAndFilter.vue';
import BulkActionsModal from '../components/BulkActionsModal.vue';
import BulkCreateUsersModal from '../components/BulkCreateUsersModal.vue';
import CreateUserModal from '../components/CreateUserModal.vue';
import ImportUsersModal from '../components/ImportUsersModal.vue';
import { useUserManagementActions } from '../composables/useUserManagementActions';
import { useUserCsvExportImport } from '../composables/useUserCsvExportImport';

const bulkModalVisible = ref(false);
const bulkCreateModalVisible = ref(false);
const createUserModalVisible = ref(false);
const importModalVisible = ref(false);
const { refreshUserData } = useUserManagementActions();
const { exportUsersCsv } = useUserCsvExportImport();

function openBulkCreateModal() {
  bulkCreateModalVisible.value = true;
}
function closeBulkCreateModal() {
  bulkCreateModalVisible.value = false;
}

// selected user count tracked locally; updated by UserTable via event
const selectedUserCount = ref(0);

// Load users automatically when page mounts
onMounted(() => {
  void refreshUserData();
});

function openBulkModal() {
  bulkModalVisible.value = true;
}
function closeBulkModal() {
  bulkModalVisible.value = false;
}

function openCreateUserModal() {
  createUserModalVisible.value = true;
}
function closeCreateUserModal() {
  createUserModalVisible.value = false;
}

function openImportModal() {
  importModalVisible.value = true;
}
function closeImportModal() {
  importModalVisible.value = false;
}

function exportUsers() {
  void exportUsersCsv();
}

function refreshUserList() {
  refreshUserData();
}
</script>